import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import type { PatientSort, SortDirection } from '~/api/types';
import type { PatientTableColumnKey } from '~/components/table/patient/types';
import { USER_ROLES } from '~/features/auth/roles';
import { ArchiveScreen } from '~/features/outpatient/ArchiveScreen';
import { OutpatientPatientsSearchParamsProvider } from '~/features/outpatient/params';
import type { OutpatientPatientsSearchParamsHandler } from '~/features/outpatient/types';
import type { ArchivePatientSearchParams } from '~/types/PatientRoute';
import { protectedRoute } from '~/utils/routesAuthorization';

export const Route = createFileRoute('/outpatient/archive')({
  component: RouteComponent,
  validateSearch: (search): ArchivePatientSearchParams<PatientSort> => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as PatientTableColumnKey | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.HcpAdmin, USER_ROLES.HcpUser],
    });
  },
});

function RouteComponent() {
  const navigate = Route.useNavigate();

  const onSearchParamsChange: OutpatientPatientsSearchParamsHandler =
    useCallback((onSearch) => navigate({ search: onSearch }), [navigate]);

  return (
    <OutpatientPatientsSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <ArchiveScreen />
    </OutpatientPatientsSearchParamsProvider>
  );
}
